import { useEffect, useState } from "react";
import { api, errMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Calendar, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { FilterPanel } from "@/components/admin/FilterPanel";
import { BulkActionsBar } from "@/components/admin/BulkActionsBar";
import { useFilters } from "@/lib/useFilters";
import { useSelection } from "@/lib/useSelection";

const SV_FILTERS = [
  { key: "search", type: "search", label: "Search school name", paths: ["school_name"] },
  { key: "status", type: "select", label: "Status", path: "status",
    options: [
      { value: "scheduled", label: "Scheduled" }, { value: "in_progress", label: "In progress" },
      { value: "completed", label: "Completed" }, { value: "cancelled", label: "Cancelled" },
    ] },
  { key: "date_from", type: "dateFrom", label: "Scheduled from", path: "scheduled_date" },
  { key: "date_to", type: "dateTo", label: "Scheduled to", path: "scheduled_date" },
];

export default function SiteVisits() {
  const { user } = useAuth();
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/site-visits");
      setVisits(data);
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); }, []);

  const filters = useFilters(visits, SV_FILTERS, { defaultSort: { field: "scheduled_date", dir: "desc" } });

  return (
    <div className="animate-fade-up" data-testid="site-visits-page">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">ACIS · Site visits</div>
        <h1 className="font-heading text-3xl font-bold text-primary flex items-center gap-3"><Calendar className="h-7 w-7" />Site visit queue</h1>
        <p className="text-sm text-slate-500 mt-1">Schools assigned for on-site review.</p>
      </div>

      <FilterPanel
        schemas={SV_FILTERS} values={filters.values} set={filters.set} clear={filters.clear}
        count={filters.count} total={visits.length} activeCount={filters.activeCount}
        sortFields={[
          { key: "scheduled_date", label: "Scheduled date" },
          { key: "school_name", label: "School name" },
        ]}
        sort={filters.sort} setSort={filters.setSort} testid="visits-filters"
      />

      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">
              <tr>
                <th className="p-4">School</th>
                <th className="p-4">Scheduled</th>
                <th className="p-4">Status</th>
                <th className="p-4">Agenda</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && <tr><td colSpan={5} className="p-8 text-center text-slate-500">Loading…</td></tr>}
              {!loading && filters.filtered.length === 0 && (
                <tr><td colSpan={5} className="p-12 text-center">
                  <Calendar className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                  <div className="font-heading font-semibold text-primary">No site visits match the current filters</div>
                </td></tr>
              )}
              {filters.filtered.map((v) => (
                <tr key={v.id} className="hover:bg-slate-50">
                  <td className="p-4 font-medium text-primary">{v.school_name}</td>
                  <td className="p-4 text-xs text-slate-700">{v.scheduled_date}</td>
                  <td className="p-4"><Badge variant="outline" className="capitalize">{v.status.replace(/_/g, " ")}</Badge></td>
                  <td className="p-4 text-xs text-slate-600 max-w-xs truncate">{v.agenda || "—"}</td>
                  <td className="p-4 text-right">
                    {(user.role === "site_visitor" || user.role === "admin") && v.status !== "completed" && (
                      <ReportDialog visit={v} onSubmitted={reload} />
                    )}
                    {v.status === "completed" && v.report && (
                      <Badge variant="outline" className="bg-emerald-50 text-emerald-800 capitalize">{v.report.overall_recommendation.replace(/_/g, " ")}</Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function ReportDialog({ visit, onSubmitted }) {
  const [open, setOpen] = useState(false);
  const [findings, setFindings] = useState("");
  const [strengths, setStrengths] = useState("");
  const [weaknesses, setWeaknesses] = useState("");
  const [rec, setRec] = useState("accredit");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!findings) { toast.error("Findings required"); return; }
    setBusy(true);
    try {
      await api.patch(`/site-visits/${visit.id}`, {
        findings,
        strengths,
        weaknesses,
        overall_recommendation: rec,
        status: "completed",
      });
      toast.success("Report submitted");
      setOpen(false);
      onSubmitted();
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`sv-report-${visit.id}`}>
          <ClipboardCheck className="h-3.5 w-3.5 mr-1" /> Submit report
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Site visit report — {visit.school_name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="mb-1.5 block">Findings</Label>
            <Textarea rows={4} value={findings} onChange={(e) => setFindings(e.target.value)} data-testid="sv-findings" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="mb-1.5 block">Strengths</Label>
              <Textarea rows={3} value={strengths} onChange={(e) => setStrengths(e.target.value)} data-testid="sv-strengths" />
            </div>
            <div>
              <Label className="mb-1.5 block">Weaknesses</Label>
              <Textarea rows={3} value={weaknesses} onChange={(e) => setWeaknesses(e.target.value)} data-testid="sv-weaknesses" />
            </div>
          </div>
          <div>
            <Label className="mb-1.5 block">Overall recommendation</Label>
            <Select value={rec} onValueChange={setRec}>
              <SelectTrigger data-testid="sv-rec"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="accredit">Accredit</SelectItem>
                <SelectItem value="accredit_with_conditions">Accredit with conditions</SelectItem>
                <SelectItem value="deny">Deny</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy} className="bg-primary text-white hover:bg-[#113A64]" data-testid="sv-submit-report">{busy ? "Submitting…" : "Submit report"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
